import React, { useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Grid,
    Paper,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useMenuLinkNavigation } from "../../hooks/useMenuLinkNavigation";
import {
    CustomPageSectionType,
    IPortalCustomPageSectionItemResponse,
    IPortalCustomPageSectionResponse,
} from "../../model/base/response/portal-custom-page-response";
import { RichTextContent } from "./rich-text-content";
import { youtubeEmbedUrl } from "../../utils/youtube";
import { ImageLightbox, LightboxImage } from "./image-lightbox";
import { LinkIconByName } from "./link-icons";
import { DocumentList } from "./document-list";

const isExternalUrl = (url: string) => /^https?:\/\//i.test(url);

/** Texto puro da descrição (itens de conteúdo misto têm descrição em HTML) para a legenda do zoom. */
const plainText = (html: string | undefined) =>
    html ? new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() || undefined : undefined;

interface ZoomableImageProps {
    src: string;
    alt: string;
    onOpen: () => void;
    sx?: object;
}

/** Imagem que amplia ao clicar (ou Enter/Espaço), com ícone de lupa no hover. */
function ZoomableImage({ src, alt, onOpen, sx }: ZoomableImageProps) {
    return (
        <Box
            role="button"
            tabIndex={0}
            aria-label={alt ? `Ampliar imagem: ${alt}` : 'Ampliar imagem'}
            onClick={onOpen}
            onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen();
                }
            }}
            sx={{
                position: 'relative',
                display: 'block',
                overflow: 'hidden',
                cursor: 'zoom-in',
                '& .zoom-hint': { opacity: 0 },
                '&:hover .zoom-hint, &:focus-visible .zoom-hint': { opacity: 1 },
                '&:hover img': { transform: 'scale(1.03)' },
                '&:focus-visible': { outline: '3px solid', outlineColor: 'brand.text', outlineOffset: 2 },
                ...sx,
            }}
        >
            <Box
                component="img"
                src={src}
                alt={alt}
                loading="lazy"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }}
            />
            <Box
                className="zoom-hint"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.35)',
                    color: 'common.white',
                    transition: 'opacity 0.2s ease',
                }}
            >
                <ZoomInIcon sx={{ fontSize: 40 }} />
            </Box>
        </Box>
    );
}

function SectionHeader({ section }: { section: IPortalCustomPageSectionResponse }) {
    if (!section.title && !section.subtitle) return null;

    return (
        <Box sx={{ mb: 3 }}>
            {section.title && (
                <Typography variant="h3" component="h2">
                    {section.title}
                </Typography>
            )}
            {section.subtitle && (
                <Typography variant="subtitle1" color="text.secondary">
                    {section.subtitle}
                </Typography>
            )}
        </Box>
    );
}

function ItemCard({ item, richDescription }: { item: IPortalCustomPageSectionItemResponse; richDescription?: boolean }) {
    const openLink = useMenuLinkNavigation();
    const [zoom, setZoom] = useState<number | null>(null);

    // Card com link leva ao destino; sem link, a imagem amplia
    const body = (
        <>
            {item.imageUrl && (item.url
                ? <CardMedia component="img" image={item.imageUrl} alt={item.title || ''} sx={{ height: 180, objectFit: 'cover' }} />
                : <ZoomableImage src={item.imageUrl} alt={item.title || ''} onOpen={() => setZoom(0)} sx={{ height: 180 }} />)}
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                        {item.title}
                    </Typography>
                    {item.url && isExternalUrl(item.url) && (
                        <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.5 }} />
                    )}
                </Box>
                {item.subtitle && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {item.subtitle}
                    </Typography>
                )}
                {item.description && (richDescription
                    ? <RichTextContent html={item.description} />
                    : <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{item.description}</Typography>)}
            </CardContent>
        </>
    );

    return (
        <Card elevation={2} sx={{ height: '100%' }}>
            {item.url
                ? <CardActionArea sx={{ height: '100%', alignItems: 'flex-start' }} onClick={() => openLink(item.url)}>{body}</CardActionArea>
                : body}
            {item.imageUrl && !item.url && (
                <ImageLightbox
                    images={[{ src: item.imageUrl, title: item.title, description: item.subtitle || plainText(item.description) }]}
                    index={zoom}
                    onChange={setZoom}
                />
            )}
        </Card>
    );
}

function ItemsGrid({ items, richDescription }: { items: IPortalCustomPageSectionItemResponse[] | undefined; richDescription?: boolean }) {
    if (!items?.length) return null;

    return (
        <Grid container spacing={3} sx={{ mt: 1 }}>
            {items.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <ItemCard item={item} richDescription={richDescription} />
                </Grid>
            ))}
        </Grid>
    );
}

function Gallery({ items }: { items: IPortalCustomPageSectionItemResponse[] | undefined }) {
    const [zoom, setZoom] = useState<number | null>(null);
    const images = (items || []).filter((item) => !!item.imageUrl);
    if (!images.length) return null;

    const lightboxImages: LightboxImage[] = images.map((item) => ({ src: item.imageUrl!, title: item.title, description: item.description }));

    return (
        <Grid container spacing={2}>
            {images.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <Box component="figure" sx={{ m: 0 }}>
                        <ZoomableImage
                            src={item.imageUrl!}
                            alt={item.title || ''}
                            onOpen={() => setZoom(index)}
                            sx={{ aspectRatio: '4 / 3', borderRadius: 1 }}
                        />
                        {(item.title || item.description) && (
                            <Box component="figcaption" sx={{ mt: 1 }}>
                                {item.title && <Typography variant="subtitle2" fontWeight={600}>{item.title}</Typography>}
                                {item.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                        {item.description}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </Grid>
            ))}
            <ImageLightbox images={lightboxImages} index={zoom} onChange={setZoom} />
        </Grid>
    );
}

function Faq({ items }: { items: IPortalCustomPageSectionItemResponse[] | undefined }) {
    if (!items?.length) return null;

    return (
        <Box>
            {items.map((item) => (
                <Accordion key={item.id} disableGutters elevation={1}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>{item.title}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{item.description}</Typography>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
}

function MixedContent({ section }: { section: IPortalCustomPageSectionResponse }) {
    const openLink = useMenuLinkNavigation();
    const [zoom, setZoom] = useState<number | null>(null);

    return (
        <>
            <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={section.imageUrl ? 7 : 12}>
                    <RichTextContent html={section.content} />
                    {section.url && (
                        <Button
                            variant="contained"
                            sx={{ mt: 2 }}
                            endIcon={isExternalUrl(section.url) ? <OpenInNewIcon /> : undefined}
                            onClick={() => openLink(section.url)}
                        >
                            Saiba mais
                        </Button>
                    )}
                </Grid>
                {section.imageUrl && (
                    <Grid item xs={12} md={5}>
                        <ZoomableImage
                            src={section.imageUrl}
                            alt={section.title || ''}
                            onOpen={() => setZoom(0)}
                            sx={{ borderRadius: 2 }}
                        />
                        <ImageLightbox
                            images={[{ src: section.imageUrl, title: section.title, description: section.subtitle }]}
                            index={zoom}
                            onChange={setZoom}
                        />
                    </Grid>
                )}
            </Grid>
            <ItemsGrid items={section.items} richDescription />
        </>
    );
}

function LinkList({ items }: { items: IPortalCustomPageSectionItemResponse[] | undefined }) {
    const openLink = useMenuLinkNavigation();
    const links = (items || []).filter((item) => !!item.url);
    if (!links.length) return null;

    return (
        <Grid container spacing={1.5} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {links.map((item) => {
                const opensNewTab = item.openInNewTab || isExternalUrl(item.url!);

                return (
                    <Grid item xs={12} md={6} key={item.id} component="li">
                        <CardActionArea
                            onClick={() => openLink(item.url, item.openInNewTab)}
                            aria-label={opensNewTab ? `${item.title} (abre em nova aba)` : item.title}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 2,
                                p: 1.5,
                                height: '100%',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                            }}
                        >
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    bgcolor: 'brand.text',
                                    color: 'primary.contrastText',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <LinkIconByName name={item.icon} />
                            </Box>
                            <Typography variant="body1" fontWeight={500} sx={{ flex: 1, wordBreak: 'break-word' }}>
                                {item.title}
                            </Typography>
                            {opensNewTab && <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                        </CardActionArea>
                    </Grid>
                );
            })}
        </Grid>
    );
}

function Video({ section }: { section: IPortalCustomPageSectionResponse }) {
    const embedUrl = youtubeEmbedUrl(section.url);

    return (
        <>
            {embedUrl && (
                <Box sx={{ position: 'relative', pt: '56.25%', borderRadius: 2, overflow: 'hidden' }}>
                    <Box
                        component="iframe"
                        src={embedUrl}
                        title={section.title || 'Vídeo'}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                    />
                </Box>
            )}
            {section.content && (
                <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{section.content}</Typography>
            )}
        </>
    );
}

function SectionBody({ section }: { section: IPortalCustomPageSectionResponse }) {
    switch (section.type) {
        case CustomPageSectionType.TextoLivre:
            return <RichTextContent html={section.content} />;
        case CustomPageSectionType.ListaItens:
            return (
                <>
                    <RichTextContent html={section.content} />
                    <ItemsGrid items={section.items} />
                </>
            );
        case CustomPageSectionType.GaleriaImagens:
            return <Gallery items={section.items} />;
        case CustomPageSectionType.Faq:
            return <Faq items={section.items} />;
        case CustomPageSectionType.ConteudoMisto:
            return <MixedContent section={section} />;
        case CustomPageSectionType.VideoEmbed:
            return <Video section={section} />;
        case CustomPageSectionType.ListaLinks:
            return (
                <>
                    <RichTextContent html={section.content} />
                    <LinkList items={section.items} />
                </>
            );
        case CustomPageSectionType.Documentos:
            return (
                <>
                    <RichTextContent html={section.content} />
                    <DocumentList items={section.items} />
                </>
            );
        default:
            return null;
    }
}

const hasContent = (section: IPortalCustomPageSectionResponse) =>
    !!(section.title || section.subtitle || section.content || section.url || section.imageUrl || section.items?.length);

/** Seções da página customizada, na ordem já definida pela API; seções vazias não geram bloco em branco. */
export function CustomPageSections({ sections }: { sections: IPortalCustomPageSectionResponse[] | undefined }) {
    const visible = sections?.filter(hasContent) || [];
    if (!visible.length) return <Alert severity="info">Esta página ainda não possui conteúdo.</Alert>;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {visible.map((section) => (
                <Paper key={section.id} component="section" variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
                    <SectionHeader section={section} />
                    <SectionBody section={section} />
                </Paper>
            ))}
        </Box>
    );
}
